import { termMaxLenderKey } from '@1delta/data-sdk'
import { parseRawAmount } from '../../../utils/parsing'
import { createMarketUid } from '../../../utils'
import { getDisplayPrice, getOraclePrice } from '../utils/oraclePrice'
import { createMultiAccountTypeUserState } from '../utils'
import {
  LenderCrossPoolMeta,
  LenderToLenderCrossPoolMeta,
  UserData,
} from '../utils/types'
import {
  getCachedTermMaxDiscovery,
  TERMMAX_MARKETS_PER_CALL,
} from './userCallBuild'

const nowSec = () => Math.floor(Date.now() / 1000)

function toBigInt(v: any): bigint {
  if (v === undefined || v === null || v === '0x') return 0n
  if (typeof v === 'bigint') return v
  try {
    return BigInt(v as any)
  } catch {
    return 0n
  }
}

/** Read a struct field by name (viem decodes named tuples) or by index. */
function field(res: any, name: string, index: number): any {
  if (res && typeof res === 'object' && !Array.isArray(res) && name in res) {
    return res[name]
  }
  if (Array.isArray(res)) return res[index]
  return undefined
}

interface ParsedGt {
  loanId: string
  collateralAmt: bigint
  debtAmt: bigint
}

/** `Position { underlyingBalance, collateralBalance, ftBalance, xtBalance, gtInfo[] }` */
function parsePosition(res: any): {
  ftBalance: bigint
  xtBalance: bigint
  gts: ParsedGt[]
} {
  const ftBalance = toBigInt(field(res, 'ftBalance', 2))
  const xtBalance = toBigInt(field(res, 'xtBalance', 3))
  const rawGts = field(res, 'gtInfo', 4)
  const gts: ParsedGt[] = []
  if (Array.isArray(rawGts)) {
    for (const g of rawGts) {
      const loanId = toBigInt(field(g, 'loanId', 0))
      // gtId 0 is the "no GT" sentinel — ids start at 1.
      if (loanId === 0n) continue
      gts.push({
        loanId: loanId.toString(),
        collateralAmt: toBigInt(field(g, 'collateralAmt', 1)),
        debtAmt: toBigInt(field(g, 'debtAmt', 2)),
      })
    }
  }
  return { ftBalance, xtBalance, gts }
}

/**
 * Returns a `[converter, expectedCallCount]` tuple for TermMax user data.
 *
 * Layout mirrors `buildTermMaxUserCall`: ONE `getPositionDetails` result, an
 * array of `Position` in the same order as the cached market layout.
 *
 * Emits one `UserData` per market key `TERMMAX_<MARKET_ADDR>`, with:
 *   - sub-account `'0'` — the LEND position: the FT balance on the loan uid.
 *     FT is a zero-coupon bond that settles 1:1 at maturity, so the balance IS
 *     the face value; it is deliberately NOT marked collateral (lending and
 *     borrowing are independent on TermMax).
 *   - one sub-account PER GT (`accountId` = decimal gtId) — the BORROW
 *     position: collateral on the collateral uid + static face-value debt on
 *     the loan uid. Debt goes in the `debtStable` slot (the fixed-term
 *     convention shared with Exactly / Lista / Teller).
 *
 * Nothing accrues on a GT — `debtAmt` is the whole story, so there is no
 * accrued-interest field to report.
 */
export const getTermMaxUserDataConverter = (
  _lender: string,
  chainId: string,
  account: string,
  meta?: LenderToLenderCrossPoolMeta,
): [(data: any[]) => { [lender: string]: UserData } | undefined, number] => {
  const discovery = getCachedTermMaxDiscovery(chainId, account)
  const markets = discovery?.markets ?? []
  // One call per disjoint market slice — the roster no longer fits in one.
  const expected = discovery?.chunks ?? 0

  return [
    (data: any[]) => {
      if (expected === 0) return undefined
      if (!data || data.length < expected) return undefined

      /**
       * Flatten the slices back into roster order. Each entry answers one
       * contiguous run of `markets`, so concatenating in call order restores the
       * index the loop below relies on — a slice that failed contributes
       * `undefined`s of the right length rather than shifting every later market
       * onto the wrong position.
       */
      const positions: any[] = []
      for (let i = 0; i < expected; i++) {
        const chunk = data[i]
        const size = Math.min(
          TERMMAX_MARKETS_PER_CALL,
          markets.length - i * TERMMAX_MARKETS_PER_CALL,
        )
        if (Array.isArray(chunk)) positions.push(...chunk)
        else positions.push(...new Array(Math.max(size, 0)).fill(undefined))
      }
      if (positions.length === 0) return undefined

      const now = nowSec()
      const out: { [lender: string]: UserData } = {}
      /** Matured positions found on-chain but unrenderable for want of meta. */
      let droppedMatured = 0

      markets.forEach((market, i) => {
        const parsed = parsePosition(positions[i])
        if (!parsed) return
        if (parsed.ftBalance === 0n && parsed.gts.length === 0) return

        const lenderKey = termMaxLenderKey(market.market)
        const metaMap: LenderCrossPoolMeta | undefined = meta?.[lenderKey]
        /**
         * NO META, NO ROW — and for a MATURED market that is currently the norm,
         * because public data is live-only by design (nobody can lend into a
         * matured market, so it has no rate, cap or availability to publish).
         *
         * The read now covers matured markets, so the position IS found here; it
         * is dropped for want of an asset/price row to render it with. Closing
         * that needs a position-only meta row per residue market — asset
         * addresses, decimals and prices, no rates — which is the one piece
         * between this and surfacing forgotten positions. Counted rather than
         * skipped in silence so the gap is measurable from the outside.
         */
        if (!metaMap) {
          if (market.maturity <= now) droppedMatured++
          return
        }

        const loanAddr = market.debtToken.toLowerCase()
        const collAddr = market.collateral.toLowerCase()
        const loanUid = createMarketUid(chainId, lenderKey, loanAddr)
        const collUid = createMarketUid(chainId, lenderKey, collAddr)

        const loanMeta = metaMap[loanUid]
        const collMeta = metaMap[collUid]
        const loanDisplay = loanMeta ? getDisplayPrice(loanMeta) : 0
        const loanOracle = loanMeta ? getOraclePrice(loanMeta) : 0
        const loanHist = loanMeta?.price?.priceUsd24h ?? loanDisplay
        const collDisplay = collMeta ? getDisplayPrice(collMeta) : 0
        const collOracle = collMeta ? getOraclePrice(collMeta) : 0
        const collHist = collMeta?.price?.priceUsd24h ?? collDisplay

        const loanDec = loanMeta?.asset?.decimals ?? market.debtDecimals
        const collDec = collMeta?.asset?.decimals ?? market.collateralDecimals

        const maturity = Number(market.maturity)
        const isMatured = maturity > 0 && maturity <= now
        const positionsByAccount: Record<string, any> = {}
        const modes: Record<string, number> = {}
        const hist: Record<string, any> = {}

        // ── sub-account '0': the LEND position (FT balance) ──
        if (parsed.ftBalance > 0n) {
          const ftStr = parseRawAmount(parsed.ftBalance.toString(), loanDec)
          const ftNum = Number(ftStr)
          positionsByAccount['0'] = {
            [loanUid]: {
              marketUid: loanUid,
              underlying: loanAddr,
              deposits: ftStr,
              debt: '0',
              debtStable: '0',
              depositsUSD: ftNum * loanDisplay,
              debtUSD: 0,
              debtStableUSD: 0,
              depositsUSDOracle: ftNum * loanOracle,
              debtUSDOracle: 0,
              debtStableUSDOracle: 0,
              stableBorrowRate: '0',
              // A TermMax lend position never collateralizes a borrow.
              collateralEnabled: false,
              claimableRewards: 0,
            },
          }
          modes['0'] = 0
          hist['0'] = { totalDeposits24h: ftNum * loanHist, totalDebt24h: 0 }
        }

        // ── one sub-account per GT: the BORROW position ──
        for (const gt of parsed.gts) {
          const collStr = parseRawAmount(gt.collateralAmt.toString(), collDec)
          const collNum = Number(collStr)
          const debtStr = parseRawAmount(gt.debtAmt.toString(), loanDec)
          const debtNum = Number(debtStr)
          if (gt.collateralAmt === 0n && gt.debtAmt === 0n) continue

          positionsByAccount[gt.loanId] = {
            [collUid]: {
              marketUid: collUid,
              underlying: collAddr,
              deposits: collStr,
              debt: '0',
              debtStable: '0',
              depositsUSD: collNum * collDisplay,
              debtUSD: 0,
              debtStableUSD: 0,
              depositsUSDOracle: collNum * collOracle,
              debtUSDOracle: 0,
              debtStableUSDOracle: 0,
              stableBorrowRate: '0',
              collateralEnabled: true,
              claimableRewards: 0,
            },
            [loanUid]: {
              marketUid: loanUid,
              underlying: loanAddr,
              deposits: '0',
              debt: '0',
              // Fixed-term debt → stable slot (Exactly / Lista / Teller convention).
              debtStable: debtStr,
              depositsUSD: 0,
              debtUSD: 0,
              debtStableUSD: debtNum * loanDisplay,
              depositsUSDOracle: 0,
              debtUSDOracle: 0,
              debtStableUSDOracle: debtNum * loanOracle,
              stableBorrowRate: '0',
              collateralEnabled: false,
              claimableRewards: 0,
              // Fixed-term loan detail. `loanId` = the gtId, which is the target
              // of every write op (repay / removeCollateral / …). Debt is a
              // STATIC face value: nothing accrues, so no accruedInterest.
              term: {
                loanId: gt.loanId,
                isDynamic: false,
                debt: debtStr,
                maturity: maturity || undefined,
                isMatured,
              },
            },
          }
          modes[gt.loanId] = 0
          hist[gt.loanId] = {
            totalDeposits24h: collNum * collHist,
            totalDebt24h: debtNum * loanHist,
          }
        }

        if (Object.keys(positionsByAccount).length === 0) return

        const userData = createMultiAccountTypeUserState(
          {
            chainId,
            account,
            lendingPositions: positionsByAccount,
            modes,
          } as any,
          metaMap,
          hist,
        )
        if (userData && userData.data.length > 0) out[lenderKey] = userData
      })

      if (droppedMatured > 0) {
        // Visible in logs rather than inferred from a missing row.
        console.warn(
          `[termmax] ${droppedMatured} matured position(s) found for ${account} on chain ${chainId} but dropped: no meta row`,
        )
      }
      return Object.keys(out).length > 0 ? out : undefined
    },
    expected,
  ]
}
